import { PermissionEnum } from '@api/enum';
import { IAddUserForm, IDocumentFileModel, IUser } from '@api/models';
import useAuthPermission from '@hooks/use-auth-permission';
import { Divider, Drawer } from '@hyperflake/react-ui-library';
import { FormHelperText, Label } from '@library/forms';
import { ErrorMessage, Form, Formik, FormikHelpers } from 'formik';
import produce from 'immer';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import UserSelectView from '../UserSelectView/UserSelectView';
import { LoadingButton } from '@library/buttons';
import { useSelector } from 'react-redux';
import { TRootState } from '@store/index';
import useAddUserToDocumentForm from 'modules/documents/hooks/useAddUserToDocumentForm';
import useRemoveUserFromDocumentForm from 'modules/documents/hooks/useRemoveUserFromDocumentForm';
import * as Yup from 'yup';
import DocumentUserRow from '../DocumentUserRow/DocumentUserRow';
import useUserListData from 'modules/users/hooks/useUserListData';

export const FileAccessSchema = Yup.object().shape({
    users: Yup.array().min(1, 'Please select at least one user.'),
});

interface DocumentAccessDrawerProps {
    clientId: string;
    show: boolean;
    onClose: () => void;
    file: IDocumentFileModel;
    onAccessUpdate: (fileId: string, updatedFile: IDocumentFileModel) => void;
    users: IUser[];
    selectedFolderId: string;
}

const DocumentAccessDrawer = (props: DocumentAccessDrawerProps) => {
    const { show, onClose, file, onAccessUpdate, users, selectedFolderId, clientId } = props;

    const { hasPermission } = useAuthPermission();

    const { info: user } = useSelector((state: TRootState) => state.profile);

    const addUserToDocumentForm = useAddUserToDocumentForm({ clientId, file, folderId: selectedFolderId || undefined });

    const removeUserFromDocumentForm = useRemoveUserFromDocumentForm({ clientId, file });

    const handleAddUsersToFile = useCallback(
        async (values: IAddUserForm, formikHelpers: FormikHelpers<IAddUserForm>) => {
            try {
                await addUserToDocumentForm.submit(values.users);

                const newUsers = users.filter((user) => values.users.includes(user._id)).map((row) => row);

                const updatedDocument = produce(file, (draftState) => {
                    draftState.users = [...(file.users as IUser[]), ...newUsers];

                    return draftState;
                });

                onAccessUpdate(file._id, updatedDocument);

                toast.success('User added successfully.');

                formikHelpers.resetForm();
            } catch (err: any) {
                console.log(err);

                toast.error(err?.data?.message || 'Some error occurred, please try again.');
            }
        },
        [addUserToDocumentForm, file, onAccessUpdate, users]
    );

    const handleRemoveUserFromDocument = useCallback(
        async (userId: string) => {
            try {
                await removeUserFromDocumentForm.submit(userId);

                const users = (file.users as IUser[]).filter((u) => u._id !== userId);

                const updatedDocument = produce(file, (draftState) => {
                    draftState.users = users;

                    return draftState;
                });

                onAccessUpdate(file._id, updatedDocument);

                toast.success('User removed successfully.');
            } catch (err: any) {
                console.log(err);

                toast.error(err?.data?.message || 'Some error occurred, please try again.');
            }
        },
        [file, onAccessUpdate, removeUserFromDocumentForm]
    );

    const fileUsers = file ? (file.users as IUser[]) : [];

    const notAddedUsers = users.filter((u) => {
        return fileUsers.every((fu) => fu._id !== u._id);
    });

    return (
        <Drawer open={show} onClose={onClose}>
            <div className="flex flex-col min-h-[calc(100dvh-3rem)]">
                <h4>{file?.name} Access</h4>

                {(hasPermission(PermissionEnum.DOCUMENT_UPDATE) || (file?.uploadedBy as IUser)?._id === user._id) &&
                    notAddedUsers.length > 0 && (
                        <>
                            <div className="mt-6">
                                <Formik
                                    initialValues={{
                                        users: [],
                                    }}
                                    validationSchema={FileAccessSchema}
                                    enableReinitialize
                                    onSubmit={handleAddUsersToFile}
                                >
                                    {({ values, setFieldValue }) => (
                                        <Form>
                                            <div>
                                                <Label htmlFor="email">Users</Label>

                                                <UserSelectView
                                                    users={notAddedUsers}
                                                    selectedUsers={values.users}
                                                    onSelectedUsersChange={(values) => setFieldValue('users', values)}
                                                />
                                                {/* 
                                                <ErrorMessage
                                                    component="div"
                                                    className="form-error-message"
                                                    name="users"
                                                /> */}
                                                <FormHelperText variant={'error'}>
                                                    <ErrorMessage name="users" />
                                                </FormHelperText>
                                            </div>

                                            <div className="mt-6">
                                                <LoadingButton
                                                    type="submit"
                                                    size="sm"
                                                    isLoading={addUserToDocumentForm.isLoading}
                                                    loadingText="Adding"
                                                >
                                                    Add
                                                </LoadingButton>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>

                            <Divider className="mt-7 mb-6" />
                        </>
                    )}

                <div className="text-grayscale-500 font-medium mt-6">Current members</div>

                {fileUsers.map((row) => (
                    <DocumentUserRow
                        uploadedBy={file.uploadedBy}
                        key={row._id}
                        user={row}
                        onAccessRevoke={() => handleRemoveUserFromDocument(row._id)}
                        isLoading={removeUserFromDocumentForm.isLoading}
                    />
                ))}
            </div>
        </Drawer>
    );
};

export default DocumentAccessDrawer;
